/**
 * The sign-in call to action: Google button, divider, email fallback.
 *
 * Also the one place a Google sign-in failure becomes visible (PIVOT-080).
 * `GoogleSignInButton` hands the popup's code to `AuthContext` and cannot
 * render an error itself — the rejection used to vanish into an unhandled
 * promise, leaving a button that just went quiet. `sessionExpired` shows here
 * too, since this is where a bounced user lands.
 */

import { Link } from 'react-router'
import { GoogleSignInButton } from '../auth/GoogleSignInButton'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n'
import '../styles/auth-forms.css'
import './SignInCta.css'

interface SignInCtaProps {
  /**
   * Whether to offer the email route as well as Google. A page showing this
   * twice (the landing page shows it in the hero and again at the close) must
   * pass `false` on the second one: two email links read as the page repeating
   * itself, and the second is never the one a visitor uses.
   *
   * The Google button *does* repeat, deliberately — the error message above it
   * has to render next to whichever button was clicked, which is why the whole
   * component repeats rather than the button alone.
   */
  emailFallback?: boolean
}

export function SignInCta({ emailFallback = true }: SignInCtaProps = {}) {
  const { t } = useI18n()
  const { error, sessionExpired } = useAuth()
  const message = error ?? (sessionExpired ? t('auth.sessionExpired') : null)

  return (
    <div className="sign-in-cta">
      {message && <p className="form-error">{message}</p>}
      <GoogleSignInButton />
      {emailFallback && (
        <>
          <div className="auth-divider">{t('auth.orDivider')}</div>
          <Link to="/login" className="btn btn-outline sign-in-cta-email">
            {t('auth.emailSignInCta')}
          </Link>
        </>
      )}
    </div>
  )
}
